import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  subscriptionPlanService,
  SubscriptionPlan,
  CreateSubscriptionPlanData,
  UpdateSubscriptionPlanData,
} from '@/services/subscription-plan.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Layout } from '@/components/Layout';
import { Plus, Edit, Power, PowerOff, Check, X } from 'lucide-react';

export function SubscriptionsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<CreateSubscriptionPlanData>({
    name: '',
    pricing: 0,
    validity: 30,
    features: [],
    maxInvitations: undefined,
    isActive: true,
  });
  const [featuresText, setFeaturesText] = useState('');
  const [validityInput, setValidityInput] = useState<string>('30');
  const [previousValidity, setPreviousValidity] = useState<number>(30);
  const [pricingInput, setPricingInput] = useState<string>('0');
  const [maxInvitationsInput, setMaxInvitationsInput] = useState<string>('');

  const queryClient = useQueryClient();

  // Function to update day mentions in features when validity changes
  const updateFeaturesWithNewValidity = (newValidity: number, oldValidity: number, currentFeatures: string) => {
    if (!currentFeatures || isNaN(newValidity) || newValidity < 1) {
      return currentFeatures;
    }

    let updatedFeatures = currentFeatures;
    
    // Strategy: Find any number that appears in a day-related context and replace it
    // This handles cases where the feature might have a different number than the old validity
    
    // First, try to replace the old validity number specifically
    // Pattern 1: "X-day" (with hyphen)
    updatedFeatures = updatedFeatures.replace(
      new RegExp(`\\b${oldValidity}(-day)`, 'gi'),
      `${newValidity}$1`
    );
    
    // Pattern 2: "X day" or "X days" (with space)
    updatedFeatures = updatedFeatures.replace(
      new RegExp(`\\b${oldValidity}(\\s+days?)`, 'gi'),
      `${newValidity}$1`
    );
    
    // If no changes were made with old validity, try to find any number in day context
    // This helps when the feature has a different number than the plan validity
    if (updatedFeatures === currentFeatures) {
      // Find any number followed by "day" or "days" and replace with new validity
      // This pattern matches: "30-day", "30 day", "30 days", etc.
      updatedFeatures = updatedFeatures.replace(
        /\b(\d+)(-day|\s+days?)\b/gi,
        (match, number, suffix) => {
          // Only replace if it looks like a day count (reasonable range: 1-365)
          const num = parseInt(number);
          if (num >= 1 && num <= 365) {
            return `${newValidity}${suffix}`;
          }
          return match;
        }
      );
    }

    return updatedFeatures;
  };

  const { data: plans = [], isLoading, refetch } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: subscriptionPlanService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: subscriptionPlanService.create,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      await refetch();
      setShowCreateModal(false);
      setFormData({ name: '', pricing: 0, validity: 30, features: [], isActive: true });
      setFeaturesText('');
      setValidityInput('30');
      setPricingInput('0');
      setSuccessMessage('Subscription plan created successfully');
      setShowSuccessModal(true);
    },
    onError: (error: any) => {
      console.error('Error creating plan:', error);
      setSuccessMessage(error.response?.data?.message || 'Failed to create subscription plan');
      setShowSuccessModal(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubscriptionPlanData }) =>
      subscriptionPlanService.update(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      await refetch();
      setShowEditModal(false);
      setSelectedPlan(null);
      setFeaturesText('');
      setSuccessMessage('Subscription plan updated successfully');
      setShowSuccessModal(true);
    },
    onError: (error: any) => {
      console.error('Error updating plan:', error);
      setSuccessMessage(error.response?.data?.message || 'Failed to update subscription plan');
      setShowSuccessModal(true);
    },
  });

  const toggleActivationMutation = useMutation({
    mutationFn: subscriptionPlanService.toggleActivation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      await refetch();
      setSuccessMessage('Subscription plan activation status updated successfully');
      setShowSuccessModal(true);
    },
    onError: (error: any) => {
      console.error('Error toggling activation:', error);
      setSuccessMessage(error.response?.data?.message || 'Failed to update activation status');
      setShowSuccessModal(true);
    },
  });

  const handleCreate = () => {
    const features = featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);

    const validity = parseInt(validityInput) || 0;
    const maxInvitations = maxInvitationsInput ? parseInt(maxInvitationsInput) : undefined;

    if (!formData.name || formData.pricing < 0 || validity < 1 || features.length === 0) {
      setSuccessMessage('Please fill in all required fields: plan name, price, validity, and features');
      setShowSuccessModal(true);
      return;
    }

    createMutation.mutate({
      ...formData,
      validity,
      features,
      maxInvitations,
    });
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      pricing: plan.pricing,
      validity: plan.validity,
      features: plan.features,
      maxInvitations: plan.maxInvitations,
      isActive: plan.isActive,
    });
    setFeaturesText(plan.features.join('\n'));
    setValidityInput(plan.validity.toString());
    setPricingInput(plan.pricing.toString());
    setMaxInvitationsInput(plan.maxInvitations?.toString() || '');
    setPreviousValidity(plan.validity);
    setShowEditModal(true);
  };

  const handleUpdate = () => {
    if (!selectedPlan) return;

    const features = featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);

    const validity = parseInt(validityInput) || 0;
    const pricing = parseFloat(pricingInput) || 0;
    const maxInvitations = maxInvitationsInput ? parseInt(maxInvitationsInput) : undefined;

    if (!formData.name || pricing < 0 || validity < 1 || features.length === 0) {
      setSuccessMessage('Please fill in all required fields');
      setShowSuccessModal(true);
      return;
    }

    updateMutation.mutate({
      id: selectedPlan.id,
      data: {
        ...formData,
        pricing,
        validity,
        features,
        maxInvitations,
      },
    });
  };

  const handleToggleActivation = (plan: SubscriptionPlan) => {
    setConfirmMessage(
      `Are you sure you want to ${plan.isActive ? 'disable' : 'enable'} the "${plan.name}" plan?`
    );
    setConfirmCallback(() => () => {
      toggleActivationMutation.mutate(plan.id);
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading subscription plans...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Subscription Plans
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              Manage subscription plans (Free Trial, Basic, Pro)
            </p>
          </div>
          <Button
            onClick={() => {
              setFormData({ name: '', pricing: 0, validity: 30, features: [], maxInvitations: undefined, isActive: true });
              setFeaturesText('');
              setPreviousValidity(30);
              setValidityInput('30');
              setPricingInput('0');
              setMaxInvitationsInput('');
              setShowCreateModal(true);
            }}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Plan
          </Button>
        </div>

        {plans.length === 0 ? (
          <Card>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>No subscription plans found.</p>
                <p className="text-sm mt-2">
                  The seeded plans (Free Trial, Basic, Pro) should appear here.
                </p>
                <p className="text-sm">Make sure the backend server has run the seed script.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <Card key={plan.id} className="relative flex flex-col">
                      <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            plan.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}
                        >
                          {plan.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="mt-2">
                        <div className="text-2xl font-bold text-foreground">
                          {formatPrice(plan.pricing)}{plan.validity === 365 ? '/year' : '/month'}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <div className="space-y-3 flex-1">
                        <div>
                          <h4 className="text-sm font-semibold mb-2 text-foreground">
                            Features:
                          </h4>
                          <ul className="space-y-1">
                            {plan.features.map((feature, index) => (
                              <li key={index} className="flex items-start text-sm">
                                <Check className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                <span className="text-muted-foreground">
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="pt-4 border-t mt-auto">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(plan)}
                            className="flex-1"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant={plan.isActive ? 'destructive' : 'default'}
                            size="sm"
                            onClick={() => handleToggleActivation(plan)}
                            className="flex-1"
                          >
                            {plan.isActive ? (
                              <>
                                <PowerOff className="h-4 w-4 mr-2" />
                                Disable
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4 mr-2" />
                                Enable
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowCreateModal(false);
              setFormData({ name: '', pricing: 0, validity: 30, features: [], maxInvitations: undefined, isActive: true });
              setFeaturesText('');
              setValidityInput('30');
              setPricingInput('0');
              setMaxInvitationsInput('');
              setPreviousValidity(30);
          }}
        >
          <Card 
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Add New Subscription Plan</CardTitle>
              <CardDescription>Create a new subscription plan</CardDescription>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCreateModal(false);
              setFormData({ name: '', pricing: 0, validity: 30, features: [], maxInvitations: undefined, isActive: true });
              setFeaturesText('');
              setValidityInput('30');
              setPricingInput('0');
              setMaxInvitationsInput('');
              setPreviousValidity(30);
                }}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan Name *</label>
                <Input
                  placeholder="e.g., Premium Plan, Starter Plan, Enterprise"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter a custom name for this subscription plan
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Pricing ($) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={pricingInput}
                    onChange={(e) => {
                      setPricingInput(e.target.value);
                      const numValue = parseFloat(e.target.value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setFormData({ ...formData, pricing: numValue });
                      }
                    }}
                    onBlur={(e) => {
                      const numValue = parseFloat(e.target.value);
                      if (isNaN(numValue) || numValue < 0) {
                        setPricingInput('0');
                        setFormData({ ...formData, pricing: 0 });
                      } else {
                        setPricingInput(numValue.toString());
                        setFormData({ ...formData, pricing: numValue });
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Validity (days) *</label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="30"
                    value={validityInput}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setValidityInput(inputValue);
                      
                      const numValue = parseInt(inputValue);
                      
                      // Always update features if we have a valid number and features text
                      if (!isNaN(numValue) && numValue > 0 && featuresText) {
                        // Update validity
                        const updatedFormData = { ...formData, validity: numValue };
                        setFormData(updatedFormData);
                        
                        // Update features if validity changed
                        if (numValue !== previousValidity) {
                          const updatedFeatures = updateFeaturesWithNewValidity(
                            numValue,
                            previousValidity,
                            featuresText
                          );
                          if (updatedFeatures !== featuresText) {
                            setFeaturesText(updatedFeatures);
                          }
                          setPreviousValidity(numValue);
                        }
                      } else if (!isNaN(numValue) && numValue > 0) {
                        // Update validity even if no features to update
                        setFormData({ ...formData, validity: numValue });
                        setPreviousValidity(numValue);
                      }
                    }}
                    onBlur={(e) => {
                      const numValue = parseInt(e.target.value);
                      if (isNaN(numValue) || numValue < 1) {
                        setValidityInput('30');
                        setFormData({ ...formData, validity: 30 });
                        setPreviousValidity(30);
                      } else {
                        setValidityInput(numValue.toString());
                        const updatedFormData = { ...formData, validity: numValue };
                        setFormData(updatedFormData);
                        
                        // Update features if validity changed
                        if (numValue !== previousValidity) {
                          const updatedFeatures = updateFeaturesWithNewValidity(
                            numValue,
                            previousValidity,
                            featuresText
                          );
                          setFeaturesText(updatedFeatures);
                          setPreviousValidity(numValue);
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Invitations (Network Members) *</label>
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 15, 25"
                  value={maxInvitationsInput}
                  onChange={(e) => {
                    setMaxInvitationsInput(e.target.value);
                    const numValue = parseInt(e.target.value);
                    if (!isNaN(numValue) && numValue > 0) {
                      setFormData({ ...formData, maxInvitations: numValue });
                    } else if (e.target.value === '') {
                      setFormData({ ...formData, maxInvitations: undefined });
                    }
                  }}
                  onBlur={(e) => {
                    const numValue = parseInt(e.target.value);
                    if (isNaN(numValue) || numValue < 1) {
                      setMaxInvitationsInput('');
                      setFormData({ ...formData, maxInvitations: undefined });
                    } else {
                      setMaxInvitationsInput(numValue.toString());
                      setFormData({ ...formData, maxInvitations: numValue });
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum number of network members that can be invited (defaults: Basic=15, Pro=25, Trial=25)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Features *</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Enter one feature per line (day mentions will auto-update when validity changes)
                </p>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="isActive" className="text-sm font-medium">
                  Active (plan will be available for selection)
                </label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
              setFormData({ name: '', pricing: 0, validity: 30, features: [], maxInvitations: undefined, isActive: true });
              setFeaturesText('');
              setValidityInput('30');
              setPricingInput('0');
              setMaxInvitationsInput('');
              setPreviousValidity(30);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="flex-1"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Plan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPlan && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
            setFeaturesText('');
            setValidityInput('30');
            setPricingInput('0');
            setPreviousValidity(30);
          }}
        >
          <Card 
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Edit Subscription Plan</CardTitle>
              <CardDescription>Update subscription plan details</CardDescription>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedPlan(null);
                  setFeaturesText('');
                  setValidityInput('30');
                  setPricingInput('0');
                  setPreviousValidity(30);
                }}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan Name *</label>
                <Input
                  placeholder="e.g., Premium Plan, Starter Plan, Enterprise"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You can update the plan name
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Pricing ($) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={pricingInput}
                    onChange={(e) => {
                      setPricingInput(e.target.value);
                      const numValue = parseFloat(e.target.value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setFormData({ ...formData, pricing: numValue });
                      }
                    }}
                    onBlur={(e) => {
                      const numValue = parseFloat(e.target.value);
                      if (isNaN(numValue) || numValue < 0) {
                        setPricingInput('0');
                        setFormData({ ...formData, pricing: 0 });
                      } else {
                        setPricingInput(numValue.toString());
                        setFormData({ ...formData, pricing: numValue });
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Validity (days) *</label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="30"
                    value={validityInput}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setValidityInput(inputValue);
                      
                      const numValue = parseInt(inputValue);
                      
                      // Always update features if we have a valid number and features text
                      if (!isNaN(numValue) && numValue > 0 && featuresText) {
                        // Update validity
                        const updatedFormData = { ...formData, validity: numValue };
                        setFormData(updatedFormData);
                        
                        // Update features if validity changed
                        if (numValue !== previousValidity) {
                          const updatedFeatures = updateFeaturesWithNewValidity(
                            numValue,
                            previousValidity,
                            featuresText
                          );
                          if (updatedFeatures !== featuresText) {
                            setFeaturesText(updatedFeatures);
                          }
                          setPreviousValidity(numValue);
                        }
                      } else if (!isNaN(numValue) && numValue > 0) {
                        // Update validity even if no features to update
                        setFormData({ ...formData, validity: numValue });
                        setPreviousValidity(numValue);
                      }
                    }}
                    onBlur={(e) => {
                      const numValue = parseInt(e.target.value);
                      if (isNaN(numValue) || numValue < 1) {
                        setValidityInput('30');
                        setFormData({ ...formData, validity: 30 });
                        setPreviousValidity(30);
                      } else {
                        setValidityInput(numValue.toString());
                        const updatedFormData = { ...formData, validity: numValue };
                        setFormData(updatedFormData);
                        
                        // Update features if validity changed
                        if (numValue !== previousValidity) {
                          const updatedFeatures = updateFeaturesWithNewValidity(
                            numValue,
                            previousValidity,
                            featuresText
                          );
                          setFeaturesText(updatedFeatures);
                          setPreviousValidity(numValue);
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Invitations (Network Members) *</label>
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 15, 25"
                  value={maxInvitationsInput}
                  onChange={(e) => {
                    setMaxInvitationsInput(e.target.value);
                    const numValue = parseInt(e.target.value);
                    if (!isNaN(numValue) && numValue > 0) {
                      setFormData({ ...formData, maxInvitations: numValue });
                    } else if (e.target.value === '') {
                      setFormData({ ...formData, maxInvitations: undefined });
                    }
                  }}
                  onBlur={(e) => {
                    const numValue = parseInt(e.target.value);
                    if (isNaN(numValue) || numValue < 1) {
                      setMaxInvitationsInput('');
                      setFormData({ ...formData, maxInvitations: undefined });
                    } else {
                      setMaxInvitationsInput(numValue.toString());
                      setFormData({ ...formData, maxInvitations: numValue });
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum number of network members that can be invited (defaults: Basic=15, Pro=25, Trial=25)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Features *</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Enter one feature per line (day mentions will auto-update when validity changes)
                </p>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="isActiveEdit" className="text-sm font-medium">
                  Active (plan will be available for selection)
                </label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPlan(null);
                    setFeaturesText('');
                    setValidityInput('30');
                    setPricingInput('0');
                    setPreviousValidity(30);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  disabled={updateMutation.isPending}
                  className="flex-1"
                >
                  {updateMutation.isPending ? 'Updating...' : 'Update Plan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSuccessModal(false)}
        >
          <Card 
            className="w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Notification</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSuccessModal(false)}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <p className="mb-4">{successMessage}</p>
              <Button onClick={() => setShowSuccessModal(false)} className="w-full">
                OK
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowConfirmModal(false)}
        >
          <Card 
            className="w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Confirm Action</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirmModal(false)}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <p className="mb-4">{confirmMessage}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button onClick={confirmCallback || undefined} className="flex-1">
                  Confirm
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}
